import React, { useCallback, useState } from "react";
import { delay } from "lodash";
import styled from "styled-components";
import {
  Color,
  Radius,
  Transition,
  ZIndexes,
  background,
  border,
  content,
  transitions,
  transparentize,
} from "@glossy";
import { Locale } from "@linkfluence/models/locale";
import { useKeyPressEvent } from "@lkf/hooks";
import Icon from "@ui-kit/icon";
import { tr } from "@radarly/i18n";

const Container = styled.div`
  background: ${background};
  border-radius: ${Radius.Small};
  color: ${content};
  box-shadow: 0 0 5px ${transparentize(0.8, Color.Black)};
  position: absolute;
  width: 600px;
  z-index: ${ZIndexes.Top};
  overflow: hidden;
  border: solid 1px ${border};
`;

const Header = styled.div`
  position: relative;
  padding: 16px;
`;

const Close = styled(Icon)`
  color: ${(props) => content(props, "secondary")};
  position: absolute;
  top: 26px;
  right: 26px;
  cursor: pointer;
  &:hover {
    color: ${content};
  }
`;

const SearchWrapper = styled.div<{ focused: boolean }>`
  background-color: ${(props) =>
    props.focused && transparentize(0.85, content(props, "primary"))};
  width: ${(props) => (props.focused ? "calc(100% - 52px)" : "65%")};
  ${transitions("width", Transition.Length)};
  position: relative;
  border-radius: 4px;
  box-sizing: border-box;
  padding-left: 42px;
  padding-right: 40px;
  white-space: nowrap;
`;

const Magnifier = styled(Icon)`
  color: ${content};
  position: absolute;
  top: 11px;
  left: 12px;
`;

const Cross = styled(Icon)`
  color: ${(props) => content(props, "secondary")};
  position: absolute;
  right: 12px;
  cursor: pointer;
  top: 12px;

  &:hover {
    color: ${content};
  }
`;

const Search = styled.input`
  background: none;
  line-height: 36px;
  height: 36px;
  width: 100%;
  color: ${content};

  &::placeholder {
    color: ${(props) => content(props, "secondary")};
  }
`;

export interface IProps {
  search: string;
  children: React.ReactNode;
  locale: Locale;
  onClose: (event: React.MouseEvent) => void;
  onChangeSearch: (search: string) => void;
}

const NavigationSwitcher = ({
  locale,
  search,
  onClose,
  onChangeSearch,
  children,
}: IProps) => {
  const [focused, setFocused] = useState(false);
  const onResetSearch = useCallback(() => {
    setFocused(false);
    delay(() => {
      onChangeSearch("");
    }, 150); // @TODO clean
  }, [onChangeSearch]);
  const onBlur = useCallback(() => {
    if (search === "") {
      setFocused(false);
    }
  }, [search]);
  useKeyPressEvent((event) => event.keyCode === 27, onResetSearch);
  return (
    <Container>
      <Header>
        <SearchWrapper focused={focused}>
          <Magnifier glyph="magnifier" size={18} />
          <Search
            placeholder={tr(locale, "SWITCHER::SEARCH_PLACEHOLDER")}
            value={search}
            onChange={(event) => onChangeSearch(event.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={onBlur}
          />
          {focused && search !== "" && (
            <Cross glyph="cross" onClick={onResetSearch} size={12} />
          )}
        </SearchWrapper>
        <Close glyph="cross" onClick={onClose} />
      </Header>
      {children}
    </Container>
  );
};

export default NavigationSwitcher;
